import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getGRIPSResponse, getGRIPSGreeting } from '../../utils/openai';
import { useAuth } from '../../hooks/AuthContext';
import { db } from '../../lib/firebase';
import DOMPurify from 'dompurify';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment,
  type FieldValue,
} from 'firebase/firestore';
import {
  Send,
  Bot,
  User,
  Heart,
  Brain,
  Shield,
  Target,
  Wind,
  Moon,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  Plus,
  Clock,
  X,
  Trash2,
  Lock,
  Unlock,
  CheckCircle,
} from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  lastMessage?: string;
  lastActivity: Date;
  messageCount: number;
  createdAt: Date;
  isLocked?: boolean;
  pinHash?: string;
}

interface QuickAction {
  id: string;
  title: string;
  icon: React.ReactNode;
  prompt: string;
  color: string;
}

// Crisis detection - client-side check for immediate support
const looksLikeCrisis = (text: string): boolean => {
  const crisisPatterns =
    /\b(kill myself|suicide|end my life|harm myself|hurt myself|kill (him|her|them)|hurt (him|her|them)|I have a plan|want to die|take my life|ending it all|no reason to live|better off without me)\b/i;
  return crisisPatterns.test(text);
};

// State-specific first responder crisis resources
// This can be expanded with more states and resources
const stateSpecificResources: Record<
  string,
  { phone?: string; website?: string; description: string }[]
> = {
  AZ: [
    { phone: '1-602-222-9444', description: 'Arizona Crisis Line (24/7)' },
    { phone: '1-800-631-1314', description: 'Arizona Peer Support Line' },
    {
      website: 'https://www.azahcccs.gov',
      description: 'Arizona Health Care Cost Containment System - Crisis Services',
    },
    {
      website: 'https://www.azdhs.gov',
      description: 'Arizona Department of Health Services - Behavioral Health Resources',
    },
  ],
  CA: [
    {
      phone: '1-855-845-7415',
      description: 'California Peer Support Foundation (First Responders)',
    },
    { website: 'https://www.capeersupport.org', description: 'CA Peer Support Network' },
  ],
  NY: [
    { phone: '1-844-863-9314', description: 'New York State Crisis Text Line' },
    {
      website: 'https://omh.ny.gov',
      description: 'NY Office of Mental Health Crisis Services',
    },
  ],
  TX: [
    { phone: '1-800-833-5948', description: 'Texas Crisis Helpline' },
    {
      website: 'https://www.hhs.texas.gov',
      description: 'TX Health & Human Services Crisis Resources',
    },
  ],
  FL: [
    { phone: '1-800-273-8255', description: 'Florida Crisis Line' },
    {
      website: 'https://www.myflfamilies.com',
      description: 'FL Behavioral Health Resources',
    },
  ],
  IL: [
    { phone: '1-800-273-8255', description: 'Illinois Crisis Line' },
    {
      website: 'https://www.dhs.state.il.us',
      description: 'IL Mental Health Crisis Services',
    },
  ],
  PA: [
    { phone: '1-800-273-8255', description: 'Pennsylvania Crisis Line' },
    {
      website: 'https://www.dhs.pa.gov',
      description: 'PA Mental Health Crisis Resources',
    },
  ],
  // Add more states as needed
};

// First responder-specific crisis resources by state/region
const getFirstResponderCrisisResources = (
  responderType?: string,
  timezone?: string,
  agency?: string | null,
): string => {
  const resources: string[] = [];

  // Universal first responder resources (always shown)
  resources.push('• Safe Call Now: 206-459-3020 (24/7 confidential line for all first responders)');
  resources.push('• Copline (Police): 1-800-267-5463 (24/7)');
  resources.push('• Fire/EMS Helpline: 1-888-731-FIRE (3473) (24/7)');
  resources.push('• National Suicide Prevention Lifeline: 988 (call or text, 24/7)');
  resources.push('• Crisis Text Line: Text HOME to 741741 (24/7)');
  resources.push('• First Responder Support Network: https://frsn.org');

  // Customize by responder type
  if (responderType === 'Firefighter') {
    resources.push('• National Fallen Firefighters Foundation: https://www.firehero.org');
    resources.push('• Firefighter Behavioral Health Alliance: https://www.ffbha.org');
  } else if (responderType === 'Police') {
    resources.push('• Blue H.E.L.P.: https://bluehelp.org');
    resources.push('• National Police Suicide Foundation: https://www.npsf.org');
  }

  // Try to determine state from timezone for state-specific resources
  // Note: This is approximate - ideally, store state explicitly in user profile
  let stateCode = '';
  if (timezone) {
    const tzLower = timezone.toLowerCase();
    // Map timezones to common states (approximate)
    // Arizona uses America/Phoenix timezone
    if (tzLower.includes('phoenix')) {
      stateCode = 'AZ';
    } else if (tzLower.includes('los_angeles')) {
      stateCode = 'CA';
    } else if (tzLower.includes('denver')) {
      stateCode = 'CO';
    } else if (tzLower.includes('chicago')) {
      stateCode = 'IL';
    } else if (tzLower.includes('new_york') || tzLower.includes('detroit')) {
      stateCode = 'NY';
    } else if (tzLower.includes('miami')) {
      stateCode = 'FL';
    }
  }

  // Add state-specific resources if available
  let stateSpecific = '';
  if (stateCode && stateSpecificResources[stateCode]) {
    stateSpecific = '\n\nLocal Resources for Your Area:';
    stateSpecificResources[stateCode].forEach((resource) => {
      if (resource.phone) {
        stateSpecific += `\n• ${resource.description}: ${resource.phone}`;
      }
      if (resource.website) {
        stateSpecific += `\n• ${resource.description}: ${resource.website}`;
      }
    });
  }

  // General guidance for local resources
  let localGuidance = '';
  if (agency || !stateCode) {
    localGuidance = '\n\nFor Additional Local Support:';
    localGuidance += "\n• Contact your department's Employee Assistance Program (EAP)";
    localGuidance += "\n• Reach out to your state's first responder peer support network";
    localGuidance +=
      '\n• Check your state or county behavioral health crisis line (often listed under your state\'s mental health authority)';
    localGuidance += '\n• Many departments have peer support teams - ask your supervisor or HR';
  }

  return resources.join('\n') + stateSpecific + localGuidance;
};

// Helper to handle inline markdown (bold, italics, links, inline code, emojis)
const formatInlineMarkdown = (text: string): string => {
  if (!text) return '';
  
  return text
    // Bold: **text** - more prominent with accent color
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-gray-900 tracking-tight bg-gradient-to-r from-gray-900 to-gray-800 bg-clip-text">$1</strong>')
    // Italic: *text* - subtle emphasis
    .replace(/(?<!\*)\*(?!\*)(.+?)\*(?!\*)/g, '<em class="italic text-gray-600 font-medium">$1</em>')
    // Inline code: `text` - badge-like appearance
    .replace(/`(.+?)`/g, '<code class="inline-flex items-center bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md text-sm font-mono border border-indigo-100 shadow-sm">$1</code>')
    // Links: [text](url) - modern button-like links
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g,
      '<a href="$2" target="_blank" class="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-semibold underline decoration-2 decoration-indigo-400 hover:decoration-indigo-600 underline-offset-2 transition-all" rel="noopener noreferrer">$1 <svg class="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></a>'
    )
    // Any remaining single newlines inside a block become <br>
    .replace(/\n/g, '<br />');
};

// BLOCK-LEVEL markdown -> HTML with enhanced styling
const formatMarkdown = (text: string): string => {
  if (!text) return '';
  
  // Pre-process: detect numbered lists that might span multiple "blocks"
  // and consolidate them before splitting
  let processedText = text;
  
  // Match patterns like "1. Item\n\n2. Item" and remove the blank line
  processedText = processedText.replace(/(\d+\.\s+[^\n]+)\n{2,}(?=\d+\.\s+)/g, '$1\n');
  
  const blocks = processedText.split(/\n{2,}/); // split on blank lines

  const htmlBlocks = blocks.map((block) => {
    const trimmed = block.trim();
    if (!trimmed) return '';

    // Heading support (#, ##, ###) - modern, card-like headers
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2];

      let tag = 'h3';
      let classes = 'text-base font-bold text-gray-900 mb-3 mt-2 pb-2 border-b border-gray-200';

      if (level === 1) {
        tag = 'h2';
        classes = 'text-lg sm:text-xl font-bold text-gray-900 mb-4 mt-3 pb-3 border-b-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-transparent -mx-2 px-2 py-2 rounded-lg';
      } else if (level === 3) {
        tag = 'h4';
        classes = 'text-sm font-semibold text-indigo-700 mb-2 mt-2 uppercase tracking-wider flex items-center space-x-2';
      }

      return `<${tag} class="${classes}">${formatInlineMarkdown(content)}</${tag}>`;
    }

    // Blockquote support ("> text") - modern callout style
    if (/^\s*>\s?/.test(trimmed)) {
      const inner = trimmed.replace(/^\s*>\s?/gm, '');
      return `
        <blockquote class="relative border-l-4 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 pl-4 pr-4 py-3 mb-4 rounded-r-xl shadow-sm transition-all hover:shadow-md hover:border-indigo-500">
          <svg class="absolute top-3 right-3 w-5 h-5 text-indigo-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
          </svg>
          <div class="text-gray-700 font-medium leading-relaxed">${formatInlineMarkdown(inner)}</div>
        </blockquote>
      `;
    }

    const lines = trimmed.split('\n');

    // Ordered list (1. 2. 3.) - also handles cases where GPT outputs all "1."
    // Check if at least half the lines look like list items to be flexible
    const listLikeLines = lines.filter((l) => /^\s*\d+\.\s+/.test(l));
    if (listLikeLines.length >= Math.max(2, lines.length * 0.5)) {
      const items = lines
        .filter((l) => /^\s*\d+\.\s+/.test(l)) // only process list items
        .map((l, idx) => {
          const content = l.replace(/^\s*\d+\.\s+/, '');
          const hasBoldHeader = /^\*\*(.+?)\*\*/.test(content);

          // Big badge style (if item begins with **bold**) - premium card design
          if (hasBoldHeader) {
            return `
              <li class="mb-5 group">
                <div class="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-br from-white to-indigo-50/30 border border-indigo-100/50 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-200">
                  <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-indigo-600 flex items-center justify-center text-white text-base font-bold shadow-md group-hover:shadow-xl group-hover:scale-110 transition-all duration-300 relative overflow-hidden">
                    <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    <span class="relative z-10">${idx + 1}</span>
                  </div>
                  <div class="flex-1 pt-1 min-w-0">
                    ${formatInlineMarkdown(content)}
                  </div>
                </div>
              </li>
            `;
          }

          // Standard numbered badge - clean, modern style
          return `
            <li class="flex items-start space-x-3 mb-3 group">
              <span class="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center text-indigo-700 text-sm font-bold border border-indigo-200/50 shadow-sm group-hover:from-indigo-200 group-hover:to-indigo-100 group-hover:border-indigo-300 group-hover:shadow transition-all duration-200">
                ${idx + 1}
              </span>
              <span class="flex-1 pt-1 pr-2 leading-relaxed">${formatInlineMarkdown(content)}</span>
            </li>
          `;
        })
        .join('');

      return `<ol class="space-y-0 mb-5">${items}</ol>`;
    }

    // Unordered list (-, *, •) - modern checkmark style
    if (lines.every((l) => /^\s*[-*•]\s+/.test(l))) {
      const items = lines
        .map((l) => {
          const content = l.replace(/^\s*[-*•]\s+/, '');

          return `
            <li class="flex items-start space-x-3 mb-2 group">
              <span class="flex-shrink-0 w-5 h-5 mt-0.5 rounded-md bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center group-hover:from-indigo-600 group-hover:to-indigo-700 transition-all duration-200 shadow-sm">
                <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                </svg>
              </span>
              <div class="flex-1 pt-0.5 text-gray-700 leading-relaxed">${formatInlineMarkdown(content)}</div>
            </li>
          `;
        })
        .join('');

      return `<ul class="space-y-1 mb-4">${items}</ul>`;
    }

    // Default paragraph - clean, readable
    return `<p class="mb-4 leading-relaxed text-gray-800">${formatInlineMarkdown(trimmed)}</p>`;
  });

  const rawHtml = htmlBlocks.join('\n');
  return DOMPurify.sanitize(rawHtml);
};

const GRIPS: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [showConversationsSidebar, setShowConversationsSidebar] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [lockedConversationId, setLockedConversationId] = useState<string | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showSetPinModal, setShowSetPinModal] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [userName, setUserName] = useState<string>('');
  const [responderType, setResponderType] = useState<string>('');
  const [shouldForceScroll, setShouldForceScroll] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHandlingIncidentRef = useRef(false);

  // Robust scroll-to-bottom function
  const forceScrollToBottom = () => {
    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const scrollNow = () => {
      const el = messageContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    };
    
    // Immediate scroll (synchronous)
    scrollNow();
    
    // Scroll after RAF (next paint)
    requestAnimationFrame(() => {
      scrollNow();
      
      // Scroll after another RAF (next frame)
      requestAnimationFrame(scrollNow);
    });
    
    // Scroll after short delay (for DOM updates)
    setTimeout(scrollNow, 50);
    
    // Scroll after animations start
    setTimeout(scrollNow, 150);
    
    // Scroll after most animations complete (fade-in is 200ms)
    setTimeout(scrollNow, 250);
    
    // Final scroll to catch any late-rendering content
    scrollTimeoutRef.current = setTimeout(scrollNow, 500);
  };
  
  // Cancel any ongoing scroll animations when user tries to interact with input
  const handleInputInteraction = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  };

  const quickActions: QuickAction[] = [
    {
      id: 'breathing',
      title: 'Breathing',
      icon: <Wind className="w-4 h-4" />,
      prompt: 'Help me with a breathing exercise to reduce stress and anxiety.',
      color: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      id: 'coping',
      title: 'Coping',
      icon: <Shield className="w-4 h-4" />,
      prompt: 'What are some effective coping strategies for dealing with work stress?',
      color: 'bg-green-500 hover:bg-green-600',
    },
    {
      id: 'goals',
      title: 'Goals',
      icon: <Target className="w-4 h-4" />,
      prompt: 'Help me set realistic mental wellness goals for this week.',
      color: 'bg-purple-500 hover:bg-purple-600',
    },
    {
      id: 'mindfulness',
      title: 'Mindfulness',
      icon: <Brain className="w-4 h-4" />,
      prompt: 'Guide me through a mindfulness exercise for grounding.',
      color: 'bg-orange-500 hover:bg-orange-600',
    },
    {
      id: 'sleep',
      title: 'Sleep',
      icon: <Moon className="w-4 h-4" />,
      prompt: 'Give me tips for better sleep hygiene and relaxation.',
      color: 'bg-indigo-500 hover:bg-indigo-600',
    },
    {
      id: 'therapy',
      title: 'Therapy',
      icon: <Heart className="w-4 h-4" />,
      prompt: 'I need help understanding when to seek professional mental health support.',
      color: 'bg-pink-500 hover:bg-pink-600',
    },
  ];

  const conversationStarters = [
    "I'm feeling overwhelmed with work stress",
    'Help me with anxiety management techniques',
    "I'm having trouble sleeping lately",
    'What are some quick stress relief methods?',
  ];

  useEffect(() => {
    // Only auto-focus on desktop (not mobile) to avoid keyboard popping up
    const isMobile =
      window.innerWidth < 1024 ||
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (!isMobile) {
      // Desktop: Auto-focus after a delay
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    // Mobile: Don't auto-focus - let user tap the input when ready
  }, []);

  // Track composer height into CSS var for scroll padding
  // Debounced to avoid spam during animations
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;

    let rafId: number | null = null;
    const setVars = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const h = el.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--composer-h', `${h}px`);
      });
    };

    setVars();
    const ro = new ResizeObserver(setVars);
    ro.observe(el);
    const onResize = () => setVars();
    window.addEventListener('resize', onResize);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // VisualViewport fallback for iOS PWAs where env(keyboard-inset-height) may be 0
  // Also freeze page scroll when keyboard is open
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const prev = { overflow: document.body.style.overflow };

    const apply = () => {
      // Height of the keyboard = how much the visual viewport shrank
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--kb', `${kb}px`);
      document.body.style.overflow = kb > 0 ? 'hidden' : prev.overflow;
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      // CRITICAL: Always restore overflow, even if prev.overflow was empty
      document.body.style.overflow = prev.overflow || '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (messages.length > 0 && showQuickActions) {
      setShowQuickActions(false);
    }
  }, [messages, showQuickActions]);

  // Only scroll when a new message is appended (not on every resize)
  const lastMessageId = messages[messages.length - 1]?.id;

  useEffect(() => {
    if (!lastMessageId) return;

    const el = messageContainerRef.current;
    if (!el) return;

    // Always scroll when shouldForceScroll is true (user sent a message or GRIPS replied)
    if (shouldForceScroll) {
      // Use setTimeout to wait for CSS animations (list item fade-in) to complete
      const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        // Also manually scroll the container as backup
        if (el) {
          setTimeout(() => {
            el.scrollTop = el.scrollHeight;
          }, 100);
        }
      };
      
      // Immediate scroll
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToBottom);
      });
      
      // Delayed scroll to catch late-rendering content (animations complete by ~350ms)
      setTimeout(scrollToBottom, 400);
      
      setShouldForceScroll(false);
      return;
    }

    // If the user is actively typing, don't auto-scroll
    if (document.activeElement === inputRef.current) return;

    // Otherwise, only scroll if user is near the bottom (don't interrupt reading)
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!nearBottom) return;

    // Smooth scroll to bottom once per new message
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [lastMessageId, shouldForceScroll]);

  const loadConversations = async () => {
    if (!user) return;
    setLoadingConversations(true);
    try {
      const conversationsRef = collection(db, 'users', user.uid, 'gripsConversations');
      const q = query(conversationsRef, orderBy('lastActivity', 'desc'));
      const snapshot = await getDocs(q);
      const loadedConversations = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title || 'New Conversation',
          lastMessage: data.lastMessage || '',
          lastActivity: data.lastActivity?.toDate() || new Date(),
          messageCount: data.messageCount || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          isLocked: data.isLocked || false,
          pinHash: data.pinHash || undefined,
        } as Conversation;
      });
      setConversations(loadedConversations);

      // Auto-load most recent unlocked conversation if none is currently loaded
      if (loadedConversations.length > 0 && !currentConversationId) {
        const unlockedConversation = loadedConversations.find((c) => !c.isLocked);
        if (unlockedConversation) {
          setCurrentConversationId(unlockedConversation.id);
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    if (!user) return;
    try {
      const messagesRef = collection(
        db,
        'users',
        user.uid,
        'gripsConversations',
        conversationId,
        'messages',
      );
      const q = query(messagesRef, orderBy('timestamp', 'asc'));
      const snapshot = await getDocs(q);
      const loadedMessages = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          role: data.role as 'user' | 'assistant',
          content: data.content,
          timestamp: data.timestamp?.toDate() || new Date(),
        } as Message;
      });
      setMessages(loadedMessages);
      setShowQuickActions(false);

      // Scroll to bottom after DOM paints
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        const el = messageContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // Fetch user's display name and responder type
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user) return;
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          
          // Set display name
          if (userData?.displayName) {
            setUserName(userData.displayName);
          } else if (user.displayName) {
            setUserName(user.displayName);
          }
          
          // Set responder type
          if (userData?.responderType) {
            setResponderType(userData.responderType);
          } else if (user.responderType) {
            setResponderType(user.responderType);
          }
        } else {
          // Fallback to auth data
          if (user.displayName) {
            setUserName(user.displayName);
          }
          if (user.responderType) {
            setResponderType(user.responderType);
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        // Fallback to auth data
        if (user.displayName) {
          setUserName(user.displayName);
        }
        if (user.responderType) {
          setResponderType(user.responderType);
        }
      }
    };
    fetchUserInfo();
  }, [user]);

  // Load conversations on mount
  useEffect(() => {
    if (!user) return;
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle initial message from incident check-in
  useEffect(() => {
    const initialMessage = location.state?.initialMessage as string | undefined;
    const incidentId = location.state?.incidentId as string | undefined;

    if (initialMessage && user && !currentConversationId && !isHandlingIncidentRef.current) {
      // Create new conversation and send the initial message
      const handleIncidentMessage = async () => {
        isHandlingIncidentRef.current = true; // Prevent duplicate runs
        
        try {
          // Create new conversation
          const conversationRef = collection(db, 'users', user.uid, 'gripsConversations');
          const newConversation = {
            title: 'Post-Incident Support',
            createdAt: serverTimestamp(),
            lastActivity: serverTimestamp(),
            messageCount: 0,
            incidentId: incidentId || null,
          };

          const docRef = await addDoc(conversationRef, newConversation);
          const conversationId = docRef.id;
          
          // Build user message
          const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: initialMessage,
            timestamp: new Date(),
          };

          // Show it immediately in UI BEFORE setting conversation ID
          setMessages([userMessage]);
          setShowQuickActions(false);
          
          // Use multiple RAF calls to ensure DOM is painted before scroll
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              forceScrollToBottom();
            });
          });
          
          // Now set conversation ID (this will trigger the other useEffect, but it will be blocked by the ref)
          setCurrentConversationId(conversationId);

          // Persist to Firestore (don't block UI on this)
          await saveMessageToFirestore(userMessage, conversationId);

          // Get GRIPS response
          setIsLoading(true);
          setIsTyping(true);

          const response = await getGRIPSResponse(
            initialMessage,
            [
              {
                role: 'user',
                content: initialMessage,
              },
            ],
            userName,
            responderType
          );

          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: response,
            timestamp: new Date(),
          };

          // Show GRIPS reply in UI
          setMessages((prev) => [...prev, assistantMessage]);
          
          // Force scroll after GRIPS message renders
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              forceScrollToBottom();
            });
          });

          // Save GRIPS reply
          await saveMessageToFirestore(assistantMessage, conversationId);

          setIsTyping(false);
          setIsLoading(false);

          // Clear location state
          window.history.replaceState({}, document.title);
        } catch (error) {
          console.error('Error handling incident message:', error);
          setIsLoading(false);
          setIsTyping(false);
        } finally {
          // Reset flag after a longer delay to ensure all effects have settled
          setTimeout(() => {
            isHandlingIncidentRef.current = false;
          }, 2000);
        }
      };

      handleIncidentMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, user, currentConversationId]);

  // Load messages when conversation changes
  useEffect(() => {
    // Skip if we're handling an incident - that flow manages its own messages
    if (isHandlingIncidentRef.current) {
      return;
    }
    
    if (currentConversationId && user) {
      // Skip if we're coming from an incident (location state still present)
      if (!location.state?.initialMessage) {
        loadConversationMessages(currentConversationId);
      }
    } else if (!currentConversationId && !location.state?.initialMessage) {
      // No active conversation - show greeting if first time
      const hasSeenGreeting = localStorage.getItem('grips-has-seen-greeting') === 'true';
      if (!hasSeenGreeting) {
        const greeting = getGRIPSGreeting(true);
        const greetingMessage: Message = {
          id: 'initial-greeting',
          role: 'assistant',
          content: greeting,
          timestamp: new Date(),
        };
        setMessages([greetingMessage]);
        localStorage.setItem('grips-has-seen-greeting', 'true');
      } else {
        setMessages([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, user]);

  // Simple hash function for PIN (not cryptographically secure, but sufficient for this use case)
  const hashPin = async (pin: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  const verifyPin = async (pin: string, hash: string): Promise<boolean> => {
    const inputHash = await hashPin(pin);
    return inputHash === hash;
  };

  const createNewConversation = async () => {
    if (!user || creatingConversation) return;

    setCreatingConversation(true);
    try {
      const conversationRef = collection(db, 'users', user.uid, 'gripsConversations');
      const newConversation = {
        title: 'New Conversation',
        lastActivity: serverTimestamp(),
        messageCount: 0,
        createdAt: serverTimestamp(),
        isLocked: false,
      };
      const docRef = await addDoc(conversationRef, newConversation);
      setCurrentConversationId(docRef.id);
      setMessages([]);
      setShowQuickActions(true);
      setShowConversationsSidebar(false);
      await loadConversations();

      // Scroll to bottom so composer lands correctly
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        const el = messageContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });

      // Visual feedback - show success state briefly
      setTimeout(() => {
        setCreatingConversation(false);
      }, 500);
    } catch (error) {
      console.error('Error creating conversation:', error);
      setCreatingConversation(false);
    }
  };

  const deleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent switching to conversation
    if (!user) return;

    if (
      !window.confirm(
        'Are you sure you want to delete this conversation? This action cannot be undone.',
      )
    ) {
      return;
    }

    try {
      // Delete all messages in the conversation
      const messagesRef = collection(
        db,
        'users',
        user.uid,
        'gripsConversations',
        conversationId,
        'messages',
      );
      const messagesSnapshot = await getDocs(messagesRef);
      const deletePromises = messagesSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));
      await Promise.all(deletePromises);

      // Delete the conversation document
      const conversationRef = doc(db, 'users', user.uid, 'gripsConversations', conversationId);
      await deleteDoc(conversationRef);

      // If this was the current conversation, clear it
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([]);
      }

      await loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const toggleLockConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;

    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation) return;

    if (conversation.isLocked) {
      // Unlock - verify PIN
      setLockedConversationId(conversationId);
      setShowPinModal(true);
      setPinInput('');
      setPinError('');
    } else {
      // Lock - set PIN
      setLockedConversationId(conversationId);
      setShowSetPinModal(true);
      setNewPin('');
      setConfirmPin('');
    }
  };

  const handleUnlockWithPin = async () => {
    if (!user || !lockedConversationId) return;

    const conversation = conversations.find((c) => c.id === lockedConversationId);
    if (!conversation || !conversation.pinHash) return;

    const isValid = await verifyPin(pinInput, conversation.pinHash);

    if (isValid) {
      setShowPinModal(false);
      const conversationIdToLoad = lockedConversationId;
      setLockedConversationId(null);
      setPinInput('');
      setPinError('');
      // Load the conversation
      setCurrentConversationId(conversationIdToLoad);
      setShowConversationsSidebar(false);
    } else {
      setPinError('Incorrect PIN. Please try again.');
      setPinInput('');
    }
  };

  const handleSetPin = async () => {
    if (!user || !lockedConversationId) return;

    if (newPin.length < 4) {
      setPinError('PIN must be at least 4 digits');
      return;
    }

    if (newPin !== confirmPin) {
      setPinError('PINs do not match');
      return;
    }

    try {
      const pinHash = await hashPin(newPin);
      const conversationRef = doc(db, 'users', user.uid, 'gripsConversations', lockedConversationId);
      await updateDoc(conversationRef, {
        isLocked: true,
        pinHash: pinHash,
      });

      await loadConversations();
      setShowSetPinModal(false);
      setLockedConversationId(null);
      setNewPin('');
      setConfirmPin('');
      setPinError('');
    } catch (error) {
      console.error('Error setting PIN:', error);
      setPinError('Failed to lock conversation. Please try again.');
    }
  };

  const saveMessageToFirestore = async (message: Message, conversationId: string) => {
    if (!user) return;
    try {
      const messagesRef = collection(
        db,
        'users',
        user.uid,
        'gripsConversations',
        conversationId,
        'messages',
      );
      await addDoc(messagesRef, {
        role: message.role,
        content: message.content,
        timestamp: Timestamp.fromDate(message.timestamp),
      });

      // Update conversation metadata
      const conversationRef = doc(db, 'users', user.uid, 'gripsConversations', conversationId);

      // Build update object with proper Firestore types
      const updateData: {
        lastActivity: ReturnType<typeof serverTimestamp>;
        messageCount: FieldValue;
        title?: string;
        lastMessage?: string;
      } = {
        lastActivity: serverTimestamp(),
        messageCount: increment(1), // Use Firestore increment to avoid drift
      };

      // If this is the first user message, generate a title
      if (message.role === 'user' && messages.filter((m) => m.role === 'user').length === 0) {
        // Use first 50 characters of first message as title
        const title = message.content.substring(0, 50).trim();
        updateData.title = title.length < message.content.length ? title + '...' : title;
        updateData.lastMessage = message.content.substring(0, 100);
      } else if (message.role === 'assistant' && messages.length > 0) {
        // Update last message preview
        updateData.lastMessage = message.content.substring(0, 100);
      }

      await updateDoc(conversationRef, updateData);

      // Reload conversations to update the list
      await loadConversations();
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;
    if (!user) return;

    // Create conversation if we don't have one
    let conversationId = currentConversationId;
    if (!conversationId) {
      try {
        const conversationRef = collection(db, 'users', user.uid, 'gripsConversations');
        const newConversation = {
          title: messageText.substring(0, 50).trim() || 'New Conversation',
          lastActivity: serverTimestamp(),
          messageCount: 0,
          createdAt: serverTimestamp(),
        };
        const docRef = await addDoc(conversationRef, newConversation);
        conversationId = docRef.id;
        setCurrentConversationId(conversationId);
        await loadConversations();
      } catch (error) {
        console.error('Error creating conversation:', error);
        return;
      }
    }

    // Crisis detection - provide immediate support
    if (looksLikeCrisis(messageText)) {
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: messageText,
        timestamp: new Date(),
      };

      // Get first responder-specific resources
      const responderType = (user as any)?.responderType;
      const timezone = (user as any)?.settings?.timezone;
      const agency = (user as any)?.agency;
      const firstResponderResources = getFirstResponderCrisisResources(
        responderType,
        timezone,
        agency,
      );

      const crisisResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I hear you, and I'm glad you reached out. This sounds really heavy—you're not alone in this.\n\nIf you're in immediate danger or having thoughts of self-harm, please:\n\n• Call 911 right away for emergency help\n• Go to your nearest emergency room\n\nFirst Responder-Specific Resources (24/7):\n${firstResponderResources}\n\nGeneral Crisis Resources:\n• 988 Suicide & Crisis Lifeline (call or text, 24/7)\n• Crisis Text Line: Text HOME to 741741 (24/7)\n\nI want to make sure you get the support you need right now. These resources are available 24/7, and there are people who understand what you're going through. You matter, and help is available.\n\nI'm still here to listen, and I hope you'll reach out to one of these resources.`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage, crisisResponse]);
      
      // Force scroll after messages render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          forceScrollToBottom();
        });
      });

      // Save both messages to Firestore
      if (conversationId) {
        await saveMessageToFirestore(userMessage, conversationId);
        await saveMessageToFirestore(crisisResponse, conversationId);
      }

      setInput('');
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      setShowQuickActions(false);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    
    // Force scroll after user message renders
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        forceScrollToBottom();
      });
    });

    // Save user message immediately
    if (conversationId) {
      await saveMessageToFirestore(userMessage, conversationId);
    }

    setInput('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setIsLoading(true);
    setIsTyping(true);
    setShowQuickActions(false);

    try {
      // Build conversation history including the just-sent user message
      const conversationHistory = [...messages, userMessage].map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      await new Promise((resolve) => setTimeout(resolve, 300));

      const response = await getGRIPSResponse(messageText, conversationHistory, userName, responderType);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Force scroll after assistant message renders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          forceScrollToBottom();
        });
      });

      // Save assistant response
      if (conversationId) {
        await saveMessageToFirestore(assistantMessage, conversationId);
      }
    } catch (error) {
      console.error('Error getting GRIPS response:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content:
          "I'm sorry, I'm having trouble responding right now. But I want you to know that reaching out shows real strength. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      
      // Force scroll after error message renders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          forceScrollToBottom();
        });
      });

      // Save error message too
      if (conversationId) {
        await saveMessageToFirestore(errorMessage, conversationId);
      }
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleSwitchConversation = async (conversationId: string) => {
    if (!user) return;

    const conversation = conversations.find((c) => c.id === conversationId);

    // Check if conversation is locked
    if (conversation?.isLocked) {
      setLockedConversationId(conversationId);
      setShowPinModal(true);
      setPinInput('');
      setPinError('');
      return;
    }

    setCurrentConversationId(conversationId);
    setShowConversationsSidebar(false);
  };

  const handleQuickAction = (action: QuickAction) => {
    // light haptic feedback on supported devices
    try {
      const nav = navigator as Navigator & { vibrate?: (pattern: number | number[]) => boolean | void };
      nav.vibrate?.(50);
    } catch {
      /* ignore haptics errors */
    }
    handleSendMessage(action.prompt);
  };

  const handleConversationStarter = (starter: string) => {
    handleSendMessage(starter);
  };

  const formatTime = (date: Date) => {
    try {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isSameMinute = (date1: Date, date2: Date) => {
    return date1.getHours() === date2.getHours() && date1.getMinutes() === date2.getMinutes();
  };

  return (
    <div
      className="flex flex-col bg-gradient-to-b from-slate-50 to-white overflow-x-hidden touch-pan-y min-w-0 lg:w-full lg:max-w-full lg:h-auto lg:min-h-screen"
      style={{
        height: '100dvh',
        minHeight: '100dvh',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Header with Conversation Management */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-20 overflow-hidden min-w-0 shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3 min-w-0">
            <button
              onClick={() => setShowConversationsSidebar(!showConversationsSidebar)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="View conversations"
            >
              <MessageSquare className="w-5 h-5 text-gray-600" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">GRIPS</h1>
              <p className="text-xs text-gray-500 truncate">
                Grit &amp; Resilience Integrated Peer Support
              </p>
            </div>
          </div>
          <button
            onClick={createNewConversation}
            disabled={creatingConversation}
            className={`p-2 rounded-lg transition-all ${
              creatingConversation ? 'bg-green-100 cursor-not-allowed' : 'hover:bg-gray-100'
            }`}
            aria-label="New conversation"
          >
            {creatingConversation ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Plus className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {/* Conversations Sidebar/Drawer */}
      {showConversationsSidebar && (
        <>
          {/* Mobile: Full screen overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setShowConversationsSidebar(false)}
          />

          {/* Sidebar */}
          <div
            className={`
              fixed top-0 left-0 w-80 bg-white shadow-xl z-40 transform transition-transform duration-300 ease-in-out
              ${showConversationsSidebar ? 'translate-x-0' : '-translate-x-full'}
              lg:fixed lg:translate-x-0 lg:shadow-none lg:border-r lg:border-gray-200
            `}
            style={{
              top: '57px', // Below header
              height: 'calc(100svh - 57px)',
            }}
          >
            <div className="flex flex-col h-full">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Conversations</h2>
                <button
                  onClick={() => setShowConversationsSidebar(false)}
                  className="p-1 hover:bg-gray-100 rounded lg:hidden"
                  aria-label="Close conversations"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* New Conversation Button */}
              <div className="p-4 border-b border-gray-200">
                <button
                  onClick={createNewConversation}
                  disabled={creatingConversation}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg transition-all ${
                    creatingConversation
                      ? 'bg-green-600 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {creatingConversation ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      <span className="font-medium">New Conversation</span>
                    </>
                  )}
                </button>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {loadingConversations ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs mt-1">Start a new conversation to begin</p>
                  </div>
                ) : (
                  <div className="p-2">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`w-full p-3 rounded-lg mb-1 transition-colors ${
                          currentConversationId === conv.id
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <button
                          onClick={() => handleSwitchConversation(conv.id)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center flex-1 min-w-0">
                              {conv.isLocked && (
                                <Lock className="w-3 h-3 text-gray-500 mr-1.5 shrink-0" />
                              )}
                              <h3 className="font-semibold text-gray-900 text-sm truncate">
                                {conv.title}
                              </h3>
                            </div>
                            {currentConversationId === conv.id && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full ml-2 shrink-0" />
                            )}
                          </div>
                          {conv.lastMessage && !conv.isLocked && (
                            <p className="text-xs text-gray-600 truncate mb-1">
                              {conv.lastMessage}
                            </p>
                          )}
                          {conv.isLocked && (
                            <p className="text-xs text-gray-500 italic mb-1">
                              Locked conversation
                            </p>
                          )}
                          <div className="flex items-center space-x-2 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>{formatRelativeTime(conv.lastActivity)}</span>
                            {conv.messageCount > 0 && (
                              <>
                                <span>•</span>
                                <span>{conv.messageCount} messages</span>
                              </>
                            )}
                          </div>
                        </button>

                        {/* Action buttons */}
                        <div className="flex items-center justify-end space-x-2 mt-2 pt-2 border-t border-gray-200">
                          <button
                            onClick={(e) => toggleLockConversation(conv.id, e)}
                            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                            aria-label={
                              conv.isLocked ? 'Unlock conversation' : 'Lock conversation'
                            }
                          >
                            {conv.isLocked ? (
                              <Unlock className="w-4 h-4 text-gray-600" />
                            ) : (
                              <Lock className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.id, e);
                            }}
                            className="p-1.5 hover:bg-red-100 rounded transition-colors"
                            aria-label="Delete conversation"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content Area - Messages Container */}
      <div
        ref={messageContainerRef}
        className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 sm:px-4 lg:px-8 py-4 space-y-4 overscroll-contain min-w-0 relative transition-all
                    ${showConversationsSidebar ? 'lg:ml-80' : 'lg:ml-0'}`}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          paddingBottom: 'calc(var(--composer-h, 96px) + 28px)',
        }}
      >
        <div className="max-w-5xl lg:max-w-4xl mx-auto">
          {messages.length === 0 ? (
            /* Welcome State */
            <div className="flex flex-col items-center justify-center min-h-full py-4 px-4 lg:py-12">
              <div className="relative mb-6 lg:mb-8">
                <div className="absolute inset-0 bg-blue-400 rounded-full blur-2xl opacity-20 animate-pulse" />
                <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 w-16 h-16 lg:w-24 lg:h-24 rounded-full flex items-center justify-center shadow-xl">
                  <Sparkles className="w-8 h-8 lg:w-12 lg:h-12 text-white" />
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 lg:mb-4 text-center">
                GRIPS
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-8 lg:mb-12 text-center max-w-md lg:max-w-2xl">
                I'm here to support you 24/7. What's on your mind?
              </p>

              {/* Quick Actions - Horizontal Scroll on Mobile, Grid on Desktop */}
              <div className="w-full max-w-2xl lg:max-w-4xl mb-6 lg:mb-10">
                <p className="text-xs lg:text-sm font-medium text-gray-500 mb-3 lg:mb-4 text-center">
                  Quick Actions
                </p>
                {/* Mobile: Horizontal Scroll */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide px-2 lg:hidden">
                  {quickActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      className={`${action.color} text-white px-4 py-2.5 rounded-full flex items-center space-x-2 shadow-md transition-all duration-200 active:scale-95 flex-shrink-0`}
                    >
                      {action.icon}
                      <span className="text-sm font-medium">{action.title}</span>
                    </button>
                  ))}
                </div>
                {/* Desktop: Grid */}
                <div className="hidden lg:grid lg:grid-cols-3 gap-3 lg:gap-4">
                  {quickActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      className={`${action.color} text-white px-6 py-4 lg:py-5 rounded-xl flex flex-col items-center justify-center space-y-2 shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-95`}
                    >
                      <span className="scale-110">{action.icon}</span>
                      <span className="text-sm lg:text-base font-semibold text-center">
                        {action.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Conversation Starters */}
              <div className="w-full max-w-md lg:max-w-4xl">
                <p className="text-xs lg:text-sm font-medium text-gray-500 mb-3 lg:mb-4 text-center">
                  Or start a conversation
                </p>
                <div className="space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-3">
                  {conversationStarters.map((starter, index) => (
                    <button
                      key={index}
                      onClick={() => handleConversationStarter(starter)}
                      className="w-full group p-3.5 lg:p-4 text-left rounded-xl bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 text-sm lg:text-base text-gray-700 shadow-sm hover:shadow-md active:scale-[0.98]"
                    >
                      <div className="flex items-center justify-between">
                        <span className="flex-1 pr-2">{starter}</span>
                        <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Messages */
            <>
              {messages.map((message, index) => {
                const showAvatar =
                  index === 0 ||
                  messages[index - 1].role !== message.role ||
                  !isSameMinute(message.timestamp, messages[index - 1].timestamp);

                return (
                  <div
                    key={message.id}
                    className={`flex w-full min-w-0 animate-fadeIn ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`flex min-w-0 max-w-[92%] sm:max-w-[85%] lg:max-w-[75%] ${
                        message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      {/* Avatar - only show when needed */}
                      {showAvatar && (
                        <div className={`${message.role === 'user' ? 'ml-2' : 'mr-2'} shrink-0`}>
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              message.role === 'user'
                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                                : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                            }`}
                          >
                            {message.role === 'user' ? (
                              <User className="w-4 h-4 text-white" />
                            ) : (
                              <Bot className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Spacer when avatar hidden */}
                      {!showAvatar && (
                        <div className={`w-10 ${message.role === 'user' ? 'ml-2' : 'mr-2'}`} />
                      )}

                      {/* Message Bubble */}
                      <div
                        className={`relative rounded-2xl shadow-sm ${
                          message.role === 'user'
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-tr-sm px-4 py-2.5'
                            : 'bg-white text-gray-900 border border-gray-200 rounded-tl-sm px-5 py-4'
                        }`}
                      >
                        <div
                          className={`break-words leading-relaxed ${
                            message.role === 'user' 
                              ? 'text-white text-sm sm:text-base [&_*]:!text-white' 
                              : 'grips-markdown text-gray-800 text-[15px] sm:text-base'
                          }`}
                          dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }}
                        />
                        <div
                          className={`flex items-center justify-end mt-1.5 space-x-1 ${
                            message.role === 'user' ? 'text-indigo-100' : 'text-gray-400'
                          }`}
                        >
                          <span className="text-[10px]">{formatTime(message.timestamp)}</span>
                          {message.role === 'user' && <CheckCircle2 className="w-3 h-3" />}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start w-full min-w-0 animate-fadeIn">
                  <div className="flex min-w-0 max-w-[85%] sm:max-w-[75%]">
                    <div className="mr-2 shrink-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                      <div className="flex space-x-1.5">
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Spacer to ensure last message is visible above input */}
          <div ref={messagesEndRef} style={{ minHeight: '1px' }} />
        </div>
      </div>

      {/* Fixed Composer (outside scroll area) */}
      <div
        ref={composerRef}
        className="
          fixed inset-x-0 bottom-0 z-30 bg-white/95 backdrop-blur-md border-t border-gray-200/50 shadow-lg
          lg:inset-x-auto lg:left-[16rem] lg:right-0
        "
        style={{
          bottom: 'max(env(keyboard-inset-height, 0px), var(--kb, 0px))',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
        }}
      >
        {/* Subtle gradient separator for "dock" effect */}
        <div className="pointer-events-none absolute -top-4 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent" />

        <div className="px-3 sm:px-4 lg:px-8 pt-3 pb-3 lg:pb-4 w-auto">
          <div className="max-w-5xl lg:max-w-4xl mx-auto">
            <div className="flex items-end space-x-2 min-w-0 bg-gray-100 rounded-2xl px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:bg-white transition-all lg:shadow-lg lg:border lg:border-gray-200">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  // Auto-resize textarea to show all content
                  const textarea = e.target;
                  textarea.style.height = 'auto';
                  const newHeight = Math.min(textarea.scrollHeight, 150);
                  textarea.style.height = `${newHeight}px`;
                }}
                onTouchStart={handleInputInteraction}
                onMouseDown={handleInputInteraction}
                onFocus={handleInputInteraction}
                onKeyDown={(e) => {
                  // Allow Enter to create new lines naturally (default textarea behavior)
                  // Users send messages using the send button for better UX

                  // Cmd/Ctrl+Enter to send on desktop
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && input.trim()) {
                    e.preventDefault();
                    handleSendMessage(input);
                    return;
                  }

                  // Shift+Enter on desktop can also send
                  if (e.key === 'Enter' && e.shiftKey && window.innerWidth >= 1024 && input.trim()) {
                    e.preventDefault();
                    handleSendMessage(input);
                    return;
                  }

                  // On mobile and regular Enter: let it create new lines naturally
                }}
                placeholder="Type your message... (Press Enter for new line, tap send to send)"
                rows={1}
                className="flex-1 min-w-0 bg-transparent border-0 focus:outline-none text-[16px] lg:text-lg placeholder:text-gray-400 resize-none overflow-y-auto leading-relaxed touch-auto"
                disabled={isLoading}
                style={{
                  minHeight: '24px',
                  maxHeight: '150px',
                  lineHeight: '1.5',
                  touchAction: 'manipulation',
                }}
              />
              <button
                onClick={() => handleSendMessage(input)}
                disabled={!input.trim() || isLoading}
                className={`shrink-0 p-2 lg:p-2.5 rounded-full transition-all duration-200 ${
                  input.trim() && !isLoading
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg active:scale-95'
                    : 'bg-gray-300 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send
                  className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${
                    input.trim() ? 'text-white' : 'text-gray-400'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PIN Entry Modal for Unlocking */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Conversation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter your PIN to access this conversation
            </p>

            <input
              type="password"
              value={pinInput}
              onChange={(e) => {
                setPinInput(e.target.value.replace(/\D/g, '').slice(0, 10));
                setPinError('');
              }}
              placeholder="Enter PIN"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pinInput.length >= 4) {
                  handleUnlockWithPin();
                }
              }}
            />

            {pinError && <p className="text-sm text-red-600 mt-2">{pinError}</p>}

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPinModal(false);
                  setLockedConversationId(null);
                  setPinInput('');
                  setPinError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUnlockWithPin}
                disabled={pinInput.length < 4}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set PIN Modal for Locking */}
      {showSetPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Lock Conversation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a PIN to protect this conversation
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter PIN (min 4 digits)
                </label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => {
                    setNewPin(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setPinError('');
                  }}
                  placeholder="Enter PIN"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-xl tracking-widest"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm PIN
                </label>
                <input
                  type="password"
                  value={confirmPin}
                  onChange={(e) => {
                    setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setPinError('');
                  }}
                  placeholder="Confirm PIN"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-xl tracking-widest"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newPin.length >= 4 && newPin === confirmPin) {
                      handleSetPin();
                    }
                  }}
                />
              </div>
            </div>

            {pinError && <p className="text-sm text-red-600 mt-2">{pinError}</p>}

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowSetPinModal(false);
                  setLockedConversationId(null);
                  setNewPin('');
                  setConfirmPin('');
                  setPinError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSetPin}
                disabled={newPin.length < 4 || newPin !== confirmPin}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Lock Conversation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GRIPS;
